import React, { useState } from 'react';
import { format } from 'date-fns';
import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import ChartContainer from '@/components/common/shared/Charts/chartContainer/ChartContainer';
import ChartHeader from '@/components/common/shared/Charts/chartHeader/ChartHeader';
import CustomTick from '@/components/common/shared/Charts/charts/CustomTick';
import { useAutoRange } from '@/components/common/shared/Charts/useAutoRange';
import { useFetchChartData } from '@/components/common/shared/Charts/useFetchChartData';
import { PricesAndChartsService } from '@/services/PricesAndChartsService';
import { CUSTOM_RANGE, TICK_GRANULARITY } from '@/utils/chartsConfig';
import {formatValue} from "@/utils/formatters";

type QuarterlyChartProps = {
  contracts: any;
  defaultContract: any;
  height?: number;
  isModalView?: boolean;
};

const QuarterlyChart = ({
  contracts,
  defaultContract,
  height = 200,
  isModalView = true,
}: QuarterlyChartProps) => {
  const [currentContract, setCurrentContract] = useState(
    contracts.indexOf(defaultContract) !== -1 ? contracts.indexOf(defaultContract) : 0
  );
  const adjustedHeight = isModalView ? 600 : height;
  const [selectedRange, setSelectedRange] = useState(CUSTOM_RANGE[3]);

  const { data, lowerLimit, upperLimit, availableRanges } = useFetchChartData(
    () =>
      PricesAndChartsService.fetchByContractAndRangeOrDate(
        contracts[currentContract],
        selectedRange
      ),
    [currentContract, selectedRange],
    () => PricesAndChartsService.fetchAvailableRanges(contracts[currentContract])
  );
  useAutoRange(data, availableRanges, selectedRange, setSelectedRange);

  const handleNextContract = () =>
    setCurrentContract((prev) => Math.min(prev + 1, contracts.length - 1));
  const handlePreviousContract = () => setCurrentContract((prev) => Math.max(prev - 1, 0));

  return (
    <div style={{ width: '100%', pointerEvents: isModalView ? 'auto' : 'none' }}>
      <ChartHeader
        ranges={CUSTOM_RANGE}
        selectedRange={selectedRange}
        onRangeChange={setSelectedRange}
        currentLabel={contracts[currentContract]}
        onPrevious={handlePreviousContract}
        onNext={handleNextContract}
        disablePrevious={currentContract === 0}
        disableNext={currentContract === contracts.length - 1}
        disabledRanges={CUSTOM_RANGE.filter((range) => !availableRanges.includes(range))}
      />
      <ChartContainer height={adjustedHeight}>
        <AreaChart data={data} height={adjustedHeight}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ffe7cd" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#ff7300" stopOpacity={0} />
            </linearGradient>
          </defs>
          {isModalView && (
            <CartesianGrid
              stroke="#aaa"
              strokeDasharray="1 1"
              vertical={true}
              horizontal={true}
              strokeOpacity={0.9}
            />
          )}
          <XAxis
            dataKey="date"
            tick={(props) => (
              <CustomTick
                {...props}
                chartType="QuarterlyChart"
                selectedRange={selectedRange}
                TICK_GRANULARITY={TICK_GRANULARITY}
              />
            )}
            tickMargin={10}
            scale="point"
            interval={0}
            axisLine={true}
            tickLine={false}
            padding={{ right: 15 }}
          />
          <YAxis
            width={80}
            domain={[lowerLimit ?? 0, upperLimit ?? 'auto']}
            tick={{ fontSize: 11 }}
            tickFormatter={(value) => formatValue(value)}
          />
          <Tooltip
            formatter={(value: any) => [`${formatValue(value)} zł/MWh`, 'Cena']}
            labelFormatter={(label) => `Data: ${format(new Date(label), 'dd.MM.yy')}`}
          />
          <text
            x={50}
            y={adjustedHeight / 2}
            textAnchor="middle"
            transform={`rotate(-90, 20, ${adjustedHeight / 2})`}
            style={{ fontSize: 10, fill: '#666' }}
          >
            [PLN/MWh]
          </text>
          <Area
            type="monotone"
            dataKey="value"
            stroke="#ff7300"
            fillOpacity={1}
            fill="url(#colorValue)"
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
};

export default QuarterlyChart;
